import React from "react";
import { useAuth } from "../context/context";

/**
 * Props for the UnauthorizedResponse component
 * @param systemAdminRequired - Whether the unauthorized action required system admin permissions
 * @param message - Optional custom message to display instead of the default ones
 * @param redirectUrl - Optional URL to redirect to when clicking the button (defaults to /login)
 */
interface UnauthorizedResponseProps {
  systemAdminRequired?: boolean;
  message?: string;
  redirectUrl?: string;
}

/**
 * A component to display when a user attempts an unauthorized action
 * Shows a message explaining the issue and provides a button to redirect
 */
const UnauthorizedResponse: React.FC<UnauthorizedResponseProps> = ({
  systemAdminRequired,
  message,
  redirectUrl = "/login",
}) => {
  /**
   * Use the global auth context to manage login state
   * This ensures consistency across components
   */
  const { setIsLoggedIn, logOutUser } = useAuth();

  /**
   * Handle unauthorized access by marking the user as logged out
   * This will hide the nav bar and other authenticated UI elements
   */
  const handleUnauthorized = () => {
    // Update the global auth state to indicate user is not logged in
    setIsLoggedIn(false);

    // Call the shared logout function
    logOutUser();
  };

  // Determine which message to show based on props
  const unauthorizedMessage =
    message ||
    (systemAdminRequired
      ? "You don't have system administrator permissions required to access this page."
      : "You are unauthorized to access this page. You either do not have permissions or your session has expired.");

  return (
    <div className="flex justify-center items-center min-h-[50vh]">
      <div className="w-full max-w-md p-6 bg-white rounded-lg shadow-md">
        <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
        <div className="px-4 pt-4 pb-2 bg-dustyGray-100 dark:bg-dustyGray-800 rounded-md">
          <p className="mb-3">{unauthorizedMessage}</p>
          <p className="mb-3">
            Use the menu to navigate to a page you are authorized to view or
            click 'Okay' to be directed to the login page.
          </p>
          <p className="mb-5">
            If you believe this is a mistake, please contact your system
            administrator.
          </p>
          <div className="text-right">
            <button
              type="button"
              className="search-results-button w-1/4 hover:bg-opacity-90 transition-all"
              onClick={handleUnauthorized}
            >
              Okay
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnauthorizedResponse;
